/**
 * Wat een wijziging in de subsidiedata met de lopende dossiers doet — berekend
 * vóór het opslaan, met de ECHTE rekenkern.
 *
 * ── WAAROM DIT BESTAAT ────────────────────────────────────────────────────
 *
 * Ronde één van T7.4 probeerde de schade te voorkomen door kolommen op slot te
 * zetten. Dat is afgekeurd, en terecht: de scepticus mat met
 * `subsidie-evaluatie-core.ts` en de echte rijen dat drie kolommen die "vrij"
 * heetten een klantofferte stil verhogen.
 *
 *   sub_regels.waarde   de `contains`-lijst van SPULA matcht vandaag niet met
 *                       het intake-vocabulaire ('N3' tegen 'N3 (>12t
 *                       vrachtwagen)'). Een collega die dat "repareert" zet
 *                       intake 6daab7ab van EUR 0 op EUR 212.000.
 *   sub_bedragen.bedrag 0,27 -> 0,40 op de MIA-27%-rij laat een ANDERE schijf
 *                       winnen, want `kiesAftrekSchijf` sorteert op `bedrag` en
 *                       kiest de laagste: EUR 89.505 -> EUR 112.590.
 *   max_bedrag          op 0 zetten is NOT NULL maar falsy; de rij verhuist naar
 *                       het gedeelde plafond en ontsnapt aan zijn eigen maximum.
 *
 * Geen van die drie is te herkennen aan de kolomnaam. Ze zijn alle drie wél te
 * herkennen aan hun UITKOMST. Stan heeft op 12-09-2026 besloten: alles vrij,
 * maar bij een wijziging die een offertebedrag verschuift laat het scherm eerst
 * zien wát er verschuift, en pas na een aparte bevestiging wordt er geschreven.
 *
 * ── EEN KOPIE VAN DE REKENKERN ZOU DE HELE OEFENING WAARDELOOS MAKEN ──────
 *
 * Dit bestand rekent met `evaluateAllSubsidies` zelf. Een tweede implementatie
 * zou precies dán afwijken wanneer het ertoe doet: bij de gevallen die niemand
 * voorzag. De import hieronder is de enige toegestane manier om aan een bedrag
 * te komen.
 *
 * Bewust vrij van React, Supabase en de `@/`-alias, zodat
 * `tests/subsidie/beheer-schrijfrecht.node.test.mjs` het rechtstreeks laadt
 * onder `node --test`. Zelfde afspraak als `subsidie-schrijfrecht.ts`,
 * `subsidie-evaluatie-core.ts` en `intake-velden.ts`.
 *
 * ── DE MEETLAT (12-09-2026, SELECT op fxlorumfwzrdwiftbalo) ───────────────
 *
 * 5 intakes, 24 matches: vier intakes hebben elk zes matches (één per
 * regeling), en één heeft er nul. Eén voorvertoning is dus 5 dossiers x 6
 * regelingen x 2 (voor en na) en duurt 0,12 ms. Er is geen reden om te
 * samplen, af te kappen of naar de server te verhuizen; dat zou de uitkomst
 * onbetrouwbaar maken voor een winst die niet bestaat.
 *
 * ALLE INTAKES TELLEN MEE, niet alleen die met een `sub_matches`-rij. Dat was
 * eerst anders, en het was fout: intake 98916b2c heeft zes gevulde datavelden
 * en volledigheid 55%, maar nul matches — hij kwam in geen enkele
 * voorvertoning voor. (De intake die wél zes matches heeft en tóch leeg is,
 * heet "Test" en heeft nul datavelden; precies andersom dus.) `sub_matches` is
 * bovendien muf: `berekend_op` staat op 2026-09-11 en Flex-E staat er op
 * EUR 39.992 waar de kern vandaag EUR 36.000 geeft. Een tabel die achterloopt
 * mag niet bepalen wát er vergeleken wordt. Bij 0,12 ms per voorvertoning is
 * "gewoon alles doorrekenen" ook goedkoper dan de vraag beantwoorden.
 */
import { evaluateAllSubsidies } from './subsidie-evaluatie-core';
import type { SubsidieIntakeData } from './useSubsidieIntake';
import type { SubsidieRegeling, SubsidieRegel, SubsidieBedrag } from './useSubsidieRegelingen';

/* ──────────────────────────────────────────────────────────────────────────
 * De drie dingen die de voorvertoning nodig heeft
 * ────────────────────────────────────────────────────────────────────────── */

/** De hele catalogus zoals de rekenkern hem leest. */
export interface Catalogus {
  regelingen: SubsidieRegeling[];
  regels: SubsidieRegel[];
  bedragen: SubsidieBedrag[];
}

/**
 * Eén dossier: één intake. Élke intake, niet alleen die met een
 * `sub_matches`-rij — zie de meetlat in de kop van dit bestand.
 */
export interface Dossier {
  intakeId: string;
  /** Wat de gebruiker het dossier noemt; valt terug op het korte uuid. */
  naam: string;
  data: SubsidieIntakeData;
}

/**
 * Wat er gaat gebeuren. Eén vorm voor alle zes handelingen, zodat er precies
 * één plek is die "wat wordt de nieuwe stand" beantwoordt.
 *
 * `id === null` betekent een INSERT. Die hoort er nadrukkelijk bij: ronde één
 * had helemaal geen slot op INSERT, en de scepticus mat dat één duplicaat-tarief
 * erbij SPULA van EUR 212.000 naar EUR 270.000 tilt.
 */
export type Voorstel =
  | { soort: 'regeling'; id: string | null; regelingId: string; rij: Partial<SubsidieRegeling> }
  | { soort: 'regel'; id: string | null; regelingId: string; rij: Partial<SubsidieRegel> }
  | { soort: 'bedrag'; id: string | null; regelingId: string; rij: Partial<SubsidieBedrag> }
  | { soort: 'regeling-verwijderen'; id: string; regelingId: string }
  | { soort: 'regel-verwijderen'; id: string; regelingId: string }
  | { soort: 'bedrag-verwijderen'; id: string; regelingId: string };

/** Een verwijdering is nooit een correctie; hij wordt altijd voorgelegd. */
export function isVerwijdering(voorstel: Voorstel): boolean {
  return voorstel.soort.endsWith('-verwijderen');
}

/**
 * Een nieuwe rij, en die wordt óók altijd voorgelegd — om twee redenen.
 *
 * Inhoudelijk: een tarief erbij zetten is geen correctie van iets bestaands.
 * De scepticus mat dat één duplicaat SPULA van EUR 212.000 naar EUR 270.000
 * tilt, en dat is precies het soort rij dat je erbij zet zonder erbij na te
 * denken.
 *
 * Mechanisch: een tweede klik op een UPDATE schrijft dezelfde waarden nog een
 * keer, maar een tweede klik op een INSERT maakt een TWEEDE RIJ. Alleen een
 * voorstel met een bevestigingssleutel is eenmalig te maken (zie
 * `bewaakBevestiging`), dus een INSERT moet er een hebben.
 */
export function isNieuweRij(voorstel: Voorstel): boolean {
  return !isVerwijdering(voorstel) && (voorstel as { id: string | null }).id === null;
}

/* ──────────────────────────────────────────────────────────────────────────
 * Kolommen die de rekenkern nooit leest
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * Uitputtend nagelopen in `subsidie-evaluatie-core.ts`: geen van deze kolommen
 * beïnvloedt een BEDRAG, een MATCH_STATUS of een AANBEVELING. Sommige worden
 * wel degelijk gelezen — `beschrijving` en `foutmelding` komen letterlijk in
 * `onvervulde_regels` en in de aanbeveling "Vul ... in om te checken" terecht —
 * maar uitsluitend als tekst die wordt doorgegeven, nooit als iets waar een
 * tak of een som van afhangt. Dat is de grens: gelezen mag, meerekenen niet.
 *
 * Een wijziging die alleen hieraan zit kan dus geen uitkomst verschuiven en
 * hoeft niet bevestigd te worden — óók niet wanneer er (nog) geen dossier is.
 *
 * Bewust KRAP gehouden. Alles wat niet met zekerheid buiten de rekenkern valt
 * gaat door de voorvertoning heen; die zegt dan gewoon "geen verschil" en kost
 * de gebruiker niets. Twee gevallen die er daarom NIET in staan hoewel het
 * verleidelijk is:
 *
 *   sub_regels.verplicht    raakt de badge, niet het bedrag — maar het verschil
 *                           tussen 'volledig' en 'gedeeltelijk' is precies het
 *                           soort gevolg dat je wilt zien voordat je het maakt.
 *   sub_bedragen.opmerkingen  ziet eruit als vrije tekst en is het vandaag ook,
 *                           maar `milieulijstLettersVanBedrag` leest er letters
 *                           uit zodra het intake-veld `milieulijst_letter`
 *                           bestaat. Vandaag rekent de voorvertoning er "geen
 *                           verschil" op; de dag dat het veld er is, niet meer.
 */
/**
 * Kolommen waarvan het gevolg PRINCIPIEEL niet te meten is met de rekenkern.
 *
 * `jaar` is er de enige, en hij is verraderlijk: `evaluateAllSubsidies` leest
 * hem nergens, dus de voorvertoning meldt keurig "geen verschil" — terwijl de
 * LEESKANT er wél op sleutelt (`useSubsidieRegelingen.ts:92`,
 * `.eq('jaar', jaar)`). EIA van 2026 naar 2027 laat die regeling uit de lijst
 * verdwijnen: gemeten EUR 112.918 over twee dossiers, weg, zonder dat er iets
 * "verandert".
 *
 * Migratie 083 zet `jaar` daarom als identiteit op slot en het formulierveld
 * staat op `disabled`, dus via het scherm is er geen knop. Maar als er ooit
 * tóch een voorstel langskomt dat hem raakt, mag dit bestand niet het
 * tegenovergestelde beweren van wat er gebeurt. Vandaar een eigen tekst en een
 * verplichte bevestiging in plaats van "geen van de 5 dossiers verandert".
 */
export const BLINDE_KOLOMMEN: Readonly<Record<'regeling' | 'regel' | 'bedrag', readonly string[]>> = {
  regeling: ['jaar'],
  regel: [],
  bedrag: [],
};

export const TOONKOLOMMEN: Readonly<Record<'regeling' | 'regel' | 'bedrag', readonly string[]>> = {
  regeling: [
    'naam', 'korte_naam', 'budget_toelichting', 'rvo_url', 'opmerkingen',
    'samenvatting', 'bron_gecontroleerd_op', 'budget_totaal', 'budget_restant',
    'budget_teller', 'aanvraag_open', 'aanvraag_sluit', 'concept_vanaf',
  ],
  regel: ['beschrijving', 'foutmelding', 'groep', 'sort_order'],
  bedrag: ['sort_order'],
};

/* ──────────────────────────────────────────────────────────────────────────
 * De nieuwe stand
 * ────────────────────────────────────────────────────────────────────────── */

function vervangOfVoegToe<T extends { id: string }>(rijen: T[], id: string | null, nieuw: T): T[] {
  if (id === null) return [...rijen, nieuw];
  return rijen.map(r => (r.id === id ? nieuw : r));
}

/**
 * De catalogus zoals hij eruit zou zien ná het voorstel.
 *
 * Een INSERT krijgt een tijdelijk id dat nergens anders voorkomt; de rekenkern
 * gebruikt `id` alleen om gekozen aftrekschijven en afgevallen vermogensbanden
 * bij te houden (`calculateAmount`), dus elk uniek id volstaat.
 */
export function bouwNaStand(voor: Catalogus, voorstel: Voorstel): Catalogus {
  const nieuwId = `voorstel-${voorstel.soort}`;

  switch (voorstel.soort) {
    case 'regeling': {
      const bestaand = voor.regelingen.find(r => r.id === voorstel.id);
      if (!bestaand) return voor;
      return {
        ...voor,
        regelingen: voor.regelingen.map(r => (r.id === voorstel.id ? { ...r, ...voorstel.rij } : r)),
      };
    }
    case 'regel': {
      const bestaand = voorstel.id ? voor.regels.find(r => r.id === voorstel.id) : null;
      const nieuw = {
        ...(bestaand ?? {}),
        ...voorstel.rij,
        id: voorstel.id ?? nieuwId,
        regeling_id: voorstel.regelingId,
      } as SubsidieRegel;
      return { ...voor, regels: vervangOfVoegToe(voor.regels, voorstel.id, nieuw) };
    }
    case 'bedrag': {
      const bestaand = voorstel.id ? voor.bedragen.find(b => b.id === voorstel.id) : null;
      const nieuw = {
        ...(bestaand ?? {}),
        ...voorstel.rij,
        id: voorstel.id ?? nieuwId,
        regeling_id: voorstel.regelingId,
      } as SubsidieBedrag;
      return { ...voor, bedragen: vervangOfVoegToe(voor.bedragen, voorstel.id, nieuw) };
    }
    case 'regel-verwijderen':
      return { ...voor, regels: voor.regels.filter(r => r.id !== voorstel.id) };
    case 'bedrag-verwijderen':
      return { ...voor, bedragen: voor.bedragen.filter(b => b.id !== voorstel.id) };
    case 'regeling-verwijderen':
      // De FK's van sub_regels en sub_bedragen staan op ON DELETE CASCADE
      // (gemeten in pg_constraint). De voorvertoning hoort dat mee te nemen,
      // anders belooft hij minder dan er gebeurt.
      return {
        regelingen: voor.regelingen.filter(r => r.id !== voorstel.id),
        regels: voor.regels.filter(r => r.regeling_id !== voorstel.id),
        bedragen: voor.bedragen.filter(b => b.regeling_id !== voorstel.id),
      };
  }
}

/* ──────────────────────────────────────────────────────────────────────────
 * De twee dingen die een bedrag verschuiven zónder dat de kolomnaam het verraadt
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * Valt deze rij onder het gedeelde percentageplafond van de regeling?
 *
 * `calculateAmount` schrijft `if (b.max_bedrag) eigenMaximum += amount; else
 * gedeeld += amount;` — een TRUTHINESS-toets. De trigger van ronde één toetste
 * `is null`, en daar zit precies één waarde tussen: 0. Een maximum van nul euro
 * is NOT NULL, dus de trigger liet hem door, maar falsy, dus de rekenkern
 * behandelde de rij alsof er geen maximum was. Gemeten op een dossier waar de
 * kap bijt: EUR 300.000 -> EUR 600.000.
 *
 * Vandaar `coalesce(x, 0) === 0` als de enige juiste lezing, aan beide kanten.
 * Migratie 083 sluit dezelfde deur nu met een CHECK: een maximum is null of
 * groter dan nul, nooit precies nul. Daarmee kunnen de twee toetsen niet meer
 * uit elkaar lopen — deze functie blijft staan omdat het scherm de wissel moet
 * kunnen benoemen, niet omdat de database hem nog kan maken.
 */
export function onderGedeeldPlafond(maxBedrag: number | null | undefined): boolean {
  return (maxBedrag ?? 0) === 0;
}

export interface PlafondWissel {
  van: 'eigen maximum' | 'gedeeld plafond';
  naar: 'eigen maximum' | 'gedeeld plafond';
  oudMax: number | null;
  nieuwMax: number | null;
}

export function plafondWisselVan(
  oud: number | null | undefined,
  nieuw: number | null | undefined,
): PlafondWissel | null {
  const oudGedeeld = onderGedeeldPlafond(oud);
  const nieuwGedeeld = onderGedeeldPlafond(nieuw);
  if (oudGedeeld === nieuwGedeeld) return null;
  return {
    van: oudGedeeld ? 'gedeeld plafond' : 'eigen maximum',
    naar: nieuwGedeeld ? 'gedeeld plafond' : 'eigen maximum',
    oudMax: oud ?? null,
    nieuwMax: nieuw ?? null,
  };
}

export interface Duplicaat {
  /** Het id van de rij die er al staat. */
  bestaandId: string;
  sleutel: string;
  /** Het label waaronder beide rijen in de specificatie zouden landen. */
  label: string;
}

/**
 * Staat er al een tarief met dezelfde vijf sleutels?
 *
 * De rekenkern groepeert op `categorie` en kiest per groep één aftrekschijf of
 * één vermogensband; buiten die twee takken tellen alle rijen gewoon op. Twee
 * identieke rijen tellen dus DUBBEL — en de specificatie toont het niet, want
 * `calculateAmount` bouwt zijn breakdown op `categorie - subcategorie` en dat
 * label is voor beide rijen gelijk. Gemeten: één duplicaat van de SPULA-rij
 * 350_550kw tilt intake 6daab7ab van EUR 212.000 naar EUR 270.000 (het
 * 20%-plafond) terwijl de breakdown ongewijzigd EUR 172.000 blijft tonen.
 *
 * Dat laatste is apart gerepareerd (de breakdown telt nu op in plaats van te
 * overschrijven), maar een duplicaat is en blijft bijna altijd een vergissing.
 * Daarom wordt hij hier bij naam genoemd, vóór het opslaan.
 */
export function vindDuplicaat(bedragen: SubsidieBedrag[], voorstel: Voorstel): Duplicaat | null {
  if (voorstel.soort !== 'bedrag') return null;
  const kandidaat = { ...voorstel.rij, regeling_id: voorstel.regelingId } as SubsidieBedrag;
  const sleutelVan = (b: Partial<SubsidieBedrag>) =>
    [b.regeling_id, b.categorie, b.subcategorie ?? '', b.bedrag_type, b.bedrijfsgrootte ?? ''].join('|');
  const sleutel = sleutelVan(kandidaat);

  const botsing = bedragen.find(b => b.id !== voorstel.id && sleutelVan(b) === sleutel);
  if (!botsing) return null;
  return {
    bestaandId: botsing.id,
    sleutel,
    label: [kandidaat.categorie, kandidaat.subcategorie].filter(Boolean).join(' - '),
  };
}

/* ──────────────────────────────────────────────────────────────────────────
 * De voorvertoning zelf
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * Wat er bij één dossier, bij één regeling verschuift.
 *
 * Niet alleen het bedrag. Twee gemeten gevolgen die een vergelijking op
 * `geschat_totaal` alléén niet ziet:
 *
 *  - `sub_regelingen.code` hernoemen haalt de EIA/MIA-stapelwaarschuwing van
 *    alle vier de kaarten. Dat is de ENIGE combinatietoets in de hele engine
 *    (`evaluateAllSubsidies`, de `unshift` onderaan), en op intake 6daab7ab
 *    gaat het om EUR 102.600 EIA náást EUR 89.505 MIA waarvan er maar één
 *    geclaimd mag worden. Geen euro verandert; de waarschuwing verdwijnt.
 *  - `sub_regels.verplicht` omzetten tilt drie dossiers van 'gedeeltelijk'
 *    naar 'volledig'. Een badge "volledig" op een dossier dat nooit getoetst
 *    is, met hetzelfde bedrag eronder.
 *
 * Vandaar de drie velden die de kern per regeling oplevert en die een mens
 * te zien krijgt: het bedrag, de badge en de adviezen.
 */
export interface Verschuiving {
  intakeId: string;
  dossier: string;
  regelingCode: string;
  oud: number;
  nieuw: number;
  verschil: number;
  oudeStatus: string;
  nieuweStatus: string;
  /** Adviezen die verdwijnen — hier zit de EIA/MIA-waarschuwing in. */
  verdwenenAdviezen: string[];
  /** Adviezen die erbij komen. */
  nieuweAdviezen: string[];
}

/** Verschuift bij deze rij het BEDRAG, of alleen de beoordeling eromheen? */
export function raaktBedrag(g: Verschuiving): boolean {
  return g.verschil !== 0;
}

export type VoorvertoningSoort =
  | 'geen-dossiers'
  | 'geen-verschil'
  | 'verschil'
  /** Een regeling die nog niet bestaat: er valt niets door te rekenen. */
  | 'nieuwe-regeling';

export interface Voorvertoning {
  soort: VoorvertoningSoort;
  /** Hoeveel dossiers er onder de geraakte regeling staan. */
  bekeken: number;
  /** Alleen de dossiers waarvan een bedrag verschuift, hoogste verschil eerst. */
  geraakt: Verschuiving[];
  duplicaat: Duplicaat | null;
  plafondWissel: PlafondWissel | null;
  /** Raakt dit voorstel uitsluitend kolommen die geen uitkomst beïnvloeden? */
  alleenToon: boolean;
  /** Is dit een verwijdering? Die is onomkeerbaar en heeft geen historie. */
  onomkeerbaar: boolean;
  /**
   * Kolommen in dit voorstel waarvan het gevolg NIET met de rekenkern te meten
   * is. Staat hier iets in, dan mag het scherm niet zeggen dat er niets
   * verandert. Zie `BLINDE_KOLOMMEN`.
   */
  blindeVlekken: string[];
  /**
   * De stand waarop deze voorvertoning is gemeten. De grendel weigert te
   * schrijven als de catalogus intussen is verschoven.
   */
  catalogusAfdruk: string;
  /**
   * De vingerafdruk van het VOORSTEL plus de gemeten uitkomst. Altijd gevuld,
   * ook als er niets te bevestigen valt: `bewaakBevestiging` gebruikt hem om
   * vast te stellen dat de rij die geschreven wordt dezelfde is als de rij
   * waarover deze voorvertoning gaat.
   */
  voorstelAfdruk: string;
  /**
   * Wat de bevestigingsknop terug moet geven. `null` = geen extra stap nodig.
   * Verandert het formulier nog één cijfer, dan verandert deze sleutel mee en
   * is een eerder gegeven bevestiging niet meer geldig.
   */
  bevestigingSleutel: string | null;
}

/**
 * De kolommen die `evaluateAllSubsidies` werkelijk leest — en dus de enige die
 * de uitkomst van een voorvertoning kunnen veranderen.
 *
 * Uitputtend nagelopen in `subsidie-evaluatie-core.ts`. Bewust NIET compleet
 * de hele rij: `naam`, `rvo_url`, `sort_order` en de budgetvelden wijzigen wel
 * de tabel maar nooit een uitkomst, en zouden de stand-controle hieronder laten
 * afgaan op wijzigingen die er niet toe doen.
 *
 * `jaar` staat er bewust niet in — de kern leest hem niet. Dát is precies
 * waarom hij als BLINDE_KOLOM apart wordt behandeld.
 */
const KERNVELDEN = {
  regeling: ['id', 'code', 'status', 'subsidie_percentage_max'],
  regel: ['id', 'regeling_id', 'veld', 'operator', 'waarde', 'verplicht', 'beschrijving', 'foutmelding'],
  bedrag: ['id', 'regeling_id', 'categorie', 'subcategorie', 'bedrag_type', 'bedrag',
    'max_bedrag', 'max_eenheden', 'min_eenheden', 'bedrijfsgrootte', 'opmerkingen'],
} as const;

export const KERNVELDEN_PER_TABEL: Readonly<Record<string, readonly string[]>> = KERNVELDEN;

/**
 * Een vingerafdruk van de STAND waarop een voorvertoning is gemeten.
 *
 * Waarom dit er moest komen. De grendel hing aan het voorstel en aan de
 * uitkomst, en `gezienOp` dekte alleen de ene rij die je schrijft. Gemeten
 * scenario met twee collega's en gewone React-Query-staleness:
 *
 *   A zet de MIA-45%-schijf op 0,30. Op A's stand wint 0,27 nog steeds, dus de
 *   voorvertoning zegt "geen verschil", er is geen dialoog en geen sleutel.
 *   B heeft intussen de 27%- en de 36%-rij VERWIJDERD — andere rijen, dus
 *   `gezienOp` op A's rij klopt nog. Werkelijk effect van A's schrijfactie:
 *   truckdepot MIA EUR 135.675 -> EUR 97.200 en 717e4678 EUR 13.108 -> EUR
 *   9.238, samen -EUR 42.345. En de grendel liet het door.
 *
 * Een voorvertoning is een meting aan een hele catalogus, niet aan één rij. De
 * afdruk gaat daarom over alle rijen die de kern leest; verschuift er ergens
 * iets, dan is de meting verlopen en wordt er niet geschreven.
 *
 * Sorteren op de gerenderde regel, niet op id: dan maakt de volgorde waarin de
 * database de rijen teruggeeft niets uit.
 */
export function catalogusAfdruk(c: Catalogus): string {
  const alsRegel = (rij: Record<string, unknown>, velden: readonly string[]) =>
    velden.map(k => `${k}=${JSON.stringify(rij[k] ?? null)}`).join('|');
  const blok = (rijen: unknown[], velden: readonly string[]) =>
    rijen.map(r => alsRegel(r as Record<string, unknown>, velden)).sort().join('\n');
  return vingerafdruk([
    blok(c.regelingen, KERNVELDEN.regeling),
    blok(c.regels, KERNVELDEN.regel),
    blok(c.bedragen, KERNVELDEN.bedrag),
  ].join('\n##\n'));
}

/** Kleine, stabiele vingerafdruk (FNV-1a). Geen crypto: dit is geen geheim. */
function vingerafdruk(tekst: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < tekst.length; i++) {
    h ^= tekst.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

/** Welke kolommen wijzigt dit voorstel werkelijk, vergeleken met de huidige rij? */
export function gewijzigdeKolommen(voor: Catalogus, voorstel: Voorstel): string[] {
  if (isVerwijdering(voorstel)) return ['*'];
  const huidig: Record<string, unknown> | undefined =
    voorstel.soort === 'regeling' ? voor.regelingen.find(r => r.id === voorstel.id) as never
      : voorstel.soort === 'regel' ? voor.regels.find(r => r.id === voorstel.id) as never
        : voor.bedragen.find(b => b.id === voorstel.id) as never;
  // Een nieuwe rij heeft geen OLD: elke gevulde kolom is een wijziging.
  if (!huidig) return Object.keys(voorstel.rij);
  return Object.keys(voorstel.rij).filter(
    k => JSON.stringify((voorstel.rij as Record<string, unknown>)[k]) !== JSON.stringify(huidig[k]),
  );
}

/**
 * Rekent het verschil uit dat dit voorstel maakt, met de echte kern.
 *
 * Alle regelingen worden per dossier vergeleken, niet alleen de geraakte. Een
 * wijziging aan regeling X hoort volgens de code alleen de bedragen van X te
 * raken, maar dat is een redenering en geen meting; door alles te vergelijken
 * komt een kruiseffect dat niemand voorzag gewoon in de lijst te staan.
 */
export function berekenVoorvertoning(
  voor: Catalogus,
  voorstel: Voorstel,
  dossiers: Dossier[],
): Voorvertoning {
  const soortSleutel: 'regeling' | 'regel' | 'bedrag' =
    voorstel.soort === 'regeling' || voorstel.soort === 'regeling-verwijderen' ? 'regeling'
      : voorstel.soort === 'regel' || voorstel.soort === 'regel-verwijderen' ? 'regel'
        : 'bedrag';
  const gewijzigd = gewijzigdeKolommen(voor, voorstel);
  const alleenToon =
    gewijzigd.length > 0 && gewijzigd.every(k => TOONKOLOMMEN[soortSleutel].includes(k));
  const blindeVlekken = gewijzigd.filter(k => BLINDE_KOLOMMEN[soortSleutel].includes(k));

  /**
   * EEN NIEUWE REGELING IS NIET DOOR TE REKENEN, EN DAT WORDT NIET VERZWEGEN.
   *
   * De verleiding is om hem in `bouwNaStand` toe te voegen en te doen alsof er
   * een voorvertoning is. Die zou "EUR 0 bij elk dossier" melden en dat is
   * misleidend: een regeling zónder regels wordt door de kern als ONGETOETST
   * behandeld (`evaluateAllSubsidies`: nul regels -> status 'gedeeltelijk' plus
   * `ongetoetst`) en keert gewoon uit zodra er één bedrag onder hangt. Het
   * getal EUR 0 zegt dus alleen "er staan nog geen bedragen onder", niet "deze
   * regeling doet niets".
   *
   * De belofte "je ziet eerst wat het doet" bestaat hier niet. Het scherm zegt
   * dat met zoveel woorden in plaats van een lege tabel te tonen.
   */
  const stand = catalogusAfdruk(voor);

  if (isNieuweRij(voorstel) && voorstel.soort === 'regeling') {
    const afdruk = vingerafdruk(JSON.stringify(['nieuwe-regeling', voorstel, stand]));
    return {
      soort: 'nieuwe-regeling',
      bekeken: dossiers.length,
      geraakt: [],
      duplicaat: null,
      plafondWissel: null,
      alleenToon: false,
      onomkeerbaar: false,
      blindeVlekken: [],
      catalogusAfdruk: stand,
      voorstelAfdruk: afdruk,
      bevestigingSleutel: afdruk,
    };
  }

  /**
   * Raakt het voorstel uitsluitend kolommen die geen uitkomst beïnvloeden, dan
   * wordt er niet doorgerekend. Dat is niet alleen sneller maar ook nodig:
   * `beschrijving` en `foutmelding` komen letterlijk in de aanbevelingen
   * terecht ("Vul ... in om te checken"), dus een vergelijking op de adviezen
   * zou élke tekstcorrectie als een verschuiving melden — en dan is de belofte
   * "teksten slaan gewoon op" weg.
   */
  const geraakt: Verschuiving[] = alleenToon ? [] : vergelijkDossiers(voor, voorstel, dossiers);

  const duplicaat = vindDuplicaat(voor.bedragen, voorstel);

  let plafondWissel: PlafondWissel | null = null;
  if (voorstel.soort === 'bedrag' && voorstel.id !== null && 'max_bedrag' in voorstel.rij) {
    const huidig = voor.bedragen.find(b => b.id === voorstel.id);
    if (huidig) plafondWissel = plafondWisselVan(huidig.max_bedrag, voorstel.rij.max_bedrag);
  }

  const soort: VoorvertoningSoort =
    geraakt.length > 0 ? 'verschil'
      : dossiers.length === 0 ? 'geen-dossiers'
        : 'geen-verschil';

  /**
   * Wanneer is een aparte bevestiging nodig?
   *
   *  - er verschuift een bedrag: altijd;
   *  - er staat al een tarief met dezelfde vijf sleutels, of de rij wisselt van
   *    plafondpotje: ook als er vandaag toevallig geen dossier onder staat dat
   *    het laat zien;
   *  - er staat GEEN enkel dossier onder deze regeling, en het voorstel raakt
   *    iets dat de rekenkern leest: dan is "geen verschil" niet te meten, en
   *    dat is iets anders dan "geen effect". De mens neemt dat besluit;
   *  - het is een VERWIJDERING: altijd, zonder uitzondering, ook bij
   *    'geen-dossiers'. Een rij weghalen is onomkeerbaar — er is geen
   *    `deleted_at` en geen historie op deze tabellen — en het kan een bedrag
   *    verhógen (haal de MIA-schijf van 27% weg en 36% is de laagste);
   *  - het is een NIEUWE RIJ: altijd. Zie `isNieuweRij`: inhoudelijk omdat een
   *    tarief erbij geen correctie is, mechanisch omdat alleen een voorstel
   *    mét sleutel eenmalig te maken is en een tweede klik op een INSERT een
   *    tweede rij oplevert.
   *
   * En omgekeerd: raakt het voorstel alleen teksten, toelichtingen of de
   * sorteervolgorde, dan is er niets te bevestigen. Dat is de belofte waar het
   * besluit van Stan op rust — het beheerscherm moet bruikbaar blijven.
   */
  const moetBevestigen =
    soort === 'verschil'
    || duplicaat !== null
    || plafondWissel !== null
    || isVerwijdering(voorstel)
    || isNieuweRij(voorstel)
    || blindeVlekken.length > 0
    || (soort === 'geen-dossiers' && !alleenToon);

  /**
   * De vingerafdruk dekt het VOORSTEL, niet alleen de uitkomst.
   *
   * Dat was een gat: `bewaakBevestiging` kreeg de velden die geschreven werden
   * nergens te zien, dus een voorvertoning van "alleen de beschrijving
   * wijzigen" (sleutel `null`) liet daarna élk payload door. Vandaag niet uit
   * te buiten via het scherm, maar de grendel bewees niets. Nu wordt hij aan
   * beide kanten uit hetzelfde voorstel herrekend.
   */
  const afdruk = vingerafdruk(JSON.stringify([voorstel, geraakt, duplicaat, plafondWissel, stand]));

  return {
    soort,
    bekeken: dossiers.length,
    geraakt,
    duplicaat,
    plafondWissel,
    alleenToon,
    onomkeerbaar: isVerwijdering(voorstel),
    blindeVlekken,
    catalogusAfdruk: stand,
    voorstelAfdruk: afdruk,
    bevestigingSleutel: moetBevestigen ? afdruk : null,
  };
}

/**
 * Legt élk dossier langs élke regeling, voor en na, met de echte kern.
 *
 * Alle regelingen, niet alleen de geraakte: een wijziging aan regeling X hoort
 * volgens de code alleen X te raken, maar dat is een redenering en geen meting.
 * Door alles te vergelijken komt een kruiseffect dat niemand voorzag gewoon in
 * de lijst — en de EIA/MIA-waarschuwing is daar het bewijs van, want die hangt
 * aan twee regelingen tegelijk.
 */
function vergelijkDossiers(voor: Catalogus, voorstel: Voorstel, dossiers: Dossier[]): Verschuiving[] {
  const na = bouwNaStand(voor, voorstel);
  const geraakt: Verschuiving[] = [];

  for (const dossier of dossiers) {
    const voorUit = evaluateAllSubsidies(dossier.data, voor.regelingen, voor.regels, voor.bedragen);
    const naUit = evaluateAllSubsidies(dossier.data, na.regelingen, na.regels, na.bedragen);
    for (const a of voorUit) {
      const b = naUit.find(x => x.regeling.id === a.regeling.id);
      // Ontbreekt de regeling in de nieuwe stand, dan is hij verwijderd en
      // valt zijn bedrag naar nul. Zonder deze tak zou het weghalen van een
      // regeling die EUR 89.505 oplevert als "geen verschil" in beeld komen.
      const nieuw = b ? b.geschat_totaal : 0;
      const nieuweStatus = b ? b.match_status : 'niet_relevant';
      const oudeAdviezen = a.aanbevelingen ?? [];
      const nieuweAdviezenAlles = b ? (b.aanbevelingen ?? []) : [];

      const verdwenenAdviezen = oudeAdviezen.filter(t => !nieuweAdviezenAlles.includes(t));
      const nieuweAdviezen = nieuweAdviezenAlles.filter(t => !oudeAdviezen.includes(t));

      const zelfde =
        nieuw === a.geschat_totaal
        && nieuweStatus === a.match_status
        && verdwenenAdviezen.length === 0
        && nieuweAdviezen.length === 0;
      if (zelfde) continue;

      geraakt.push({
        intakeId: dossier.intakeId,
        dossier: dossier.naam,
        regelingCode: a.regeling.code,
        oud: a.geschat_totaal,
        nieuw,
        verschil: nieuw - a.geschat_totaal,
        oudeStatus: a.match_status,
        nieuweStatus,
        verdwenenAdviezen,
        nieuweAdviezen,
      });
    }
  }

  // Geld eerst, en binnen gelijk bedrag op dossiernaam zodat de volgorde
  // stabiel is (de vingerafdruk hangt eraan).
  geraakt.sort(
    (a, b) => Math.abs(b.verschil) - Math.abs(a.verschil)
      || a.dossier.localeCompare(b.dossier)
      || a.regelingCode.localeCompare(b.regelingCode),
  );
  return geraakt;
}

/* ──────────────────────────────────────────────────────────────────────────
 * Wat het scherm erover zegt
 * ────────────────────────────────────────────────────────────────────────── */

export function alsEuro(bedrag: number): string {
  return `€ ${Math.round(bedrag).toLocaleString('nl-NL')}`;
}

/**
 * De zin op de bevestigingsknop. Het BEDRAG staat erin, niet "er verandert
 * iets" — een vinkje dat je wegklikt is geen besluit.
 */
export function bevestigingsTekst(v: Voorvertoning): string {
  // Een verwijdering is onomkeerbaar; dat hoort op de knop te staan en niet
  // alleen in een alinea erboven. Er is geen `deleted_at` en geen historie op
  // sub_regels en sub_bedragen — weg is weg.
  const staart = v.onomkeerbaar ? 'Definitief verwijderen?' : 'Opslaan?';

  // Een blinde vlek gaat vóór alles: hier mag níét staan dat er niets
  // verandert, want dat is aantoonbaar onwaar.
  if (v.blindeVlekken.length > 0) {
    return (
      `Wat dit doet is hier niet te meten: ${v.blindeVlekken.join(' en ')} wordt door de rekenkern niet `
      + `gelezen, maar de leeskant selecteert er wél op. ${staart}`
    );
  }

  if (v.soort === 'nieuwe-regeling') {
    return (
      'Een nieuwe regeling is niet vooruit door te rekenen: zonder toetsingsregels kwalificeert hij bij '
      + `elk dossier, en er staan nog geen bedragen onder. ${staart}`
    );
  }
  if (v.soort === 'verschil') {
    const metGeld = v.geraakt.filter(raaktBedrag);
    const n = v.geraakt.length;
    const dossiers = `${n} ${n === 1 ? 'dossier' : 'dossiers'}`;

    // Verandert er geen euro maar wél een badge of een waarschuwing, dan mag de
    // zin dat niet als bedrag verpakken. "€ 89.505 → € 89.505" leest als een
    // fout; het gevolg zit ergens anders.
    if (metGeld.length === 0) {
      const g = v.geraakt[0];
      const wat = g.oudeStatus !== g.nieuweStatus
        ? `de beoordeling gaat van "${g.oudeStatus}" naar "${g.nieuweStatus}"`
        : 'er verdwijnt of verschijnt een waarschuwing bij de regeling';
      return `Geen bedrag verandert, maar bij ${dossiers} ${wat}. ${staart}`;
    }

    if (metGeld.length === 1) {
      const g = metGeld[0];
      const rest = n > 1 ? ` (en bij ${n - 1} ${n - 1 === 1 ? 'dossier' : 'dossiers'} de beoordeling)` : '';
      return `Dit verandert 1 dossier: ${alsEuro(g.oud)} → ${alsEuro(g.nieuw)}${rest}. ${staart}`;
    }
    const eerste = metGeld[0];
    const som = metGeld.reduce((t, g) => t + g.verschil, 0);
    return (
      `Dit verandert ${metGeld.length} dossiers, samen ${som >= 0 ? '+' : '−'}${alsEuro(Math.abs(som))}. `
      + `Grootste: ${eerste.dossier} ${alsEuro(eerste.oud)} → ${alsEuro(eerste.nieuw)}. ${staart}`
    );
  }
  if (v.soort === 'geen-dossiers') {
    return (
      'Er is vandaag geen enkel dossier om tegen te vergelijken, dus er is niets gemeten — '
      + 'dat is iets anders dan "geen effect". Wat je hier wijzigt telt mee zodra het eerste dossier '
      + `erbij komt. ${staart}`
    );
  }
  return `Geen van de ${v.bekeken} dossiers verandert van bedrag of beoordeling. ${staart}`;
}

/** De losse waarschuwingen die naast het verschil staan. */
export function voorvertoningWaarschuwingen(v: Voorvertoning): string[] {
  const uit: string[] = [];
  if (v.blindeVlekken.includes('jaar')) {
    uit.push(
      'Het jaar van een regeling verplaatsen is GEEN gewone wijziging, en de voorvertoning hierboven kan '
      + 'hem niet meten: de rekenkern leest `jaar` nergens, maar de leeskant selecteert erop '
      + '(useSubsidieRegelingen.ts:92). De regeling verdwijnt daarmee mét al zijn regels en bedragen uit '
      + 'de lijst van dit jaar. Gemeten op EIA: € 112.918 over twee dossiers, weg — zonder dat er iets '
      + '"verandert". Maak liever een nieuwe regeling aan voor het nieuwe jaar.',
    );
  }
  if (v.soort === 'nieuwe-regeling') {
    uit.push(
      'Een regeling ZONDER toetsingsregels kwalificeert bij elk dossier. De rekenkern behandelt hem als '
      + 'ongetoetst en rekent gewoon een bedrag uit — er is dan niets gecontroleerd. Voeg dus eerst de '
      + 'voorwaarden toe en pas daarna de bedragen.',
    );
    uit.push(
      'Wat deze regeling met de lopende dossiers doet, is nu niet te tonen: er staan nog geen regels en '
      + 'geen bedragen onder. Zodra je die toevoegt, zie je bij élke volgende stap wél wat er verschuift.',
    );
  }
  if (v.onomkeerbaar) {
    uit.push(
      'Verwijderen kan niet ongedaan worden gemaakt. Deze tabellen kennen geen "verwijderd op" en geen '
      + 'historie: de rij is weg, en met hem de reden waarom hij er stond. Twijfel je, zet het bedrag dan '
      + 'op 0 of pas de voorwaarde aan — dan blijft het spoor bestaan.',
    );
  }
  if (v.duplicaat) {
    uit.push(
      `Er staat al een tarief met dezelfde categorie, subcategorie, soort en bedrijfsgrootte `
      + `("${v.duplicaat.label}"). Twee zulke rijen tellen allebei mee in het totaal. `
      + 'Bedoelde je dit tarief te WIJZIGEN in plaats van er een tweede naast te zetten?',
    );
  }
  if (v.plafondWissel) {
    const w = v.plafondWissel;
    uit.push(
      `Deze rij verhuist van "${w.van}" naar "${w.naar}". De aanwezigheid van een maximum bepaalt of dit `
      + 'onderdeel onder het regelingbrede percentageplafond valt; een maximum van 0 telt daarbij als géén '
      + 'maximum. Dat is een andere rekenwijze, geen correctie.',
    );
  }
  return uit;
}

/* ──────────────────────────────────────────────────────────────────────────
 * Zonder bevestiging wordt er niets geschreven
 * ────────────────────────────────────────────────────────────────────────── */

export class NietBevestigd extends Error {
  readonly soort = 'niet-bevestigd' as const;
  constructor(melding: string) {
    super(melding);
    this.name = 'NietBevestigd';
  }
}

/**
 * Sleutels die al één keer een schrijfactie hebben gedekt.
 *
 * Waarom dit er is: dezelfde sleutel werd drie van de drie keer geaccepteerd.
 * Bij een UPDATE is een tweede vuur onschadelijk — dezelfde waarden nog een
 * keer — maar bij een INSERT is het een TWEEDE RIJ, en juist een duplicaat is
 * het geval dat de scepticus mat (SPULA EUR 212.000 -> EUR 270.000). Een
 * bevestiging hoort bij één handeling, niet bij een soort handeling.
 *
 * Modulestatus in een verder pure module, en dat is een bewuste uitzondering:
 * eenmaligheid ís state, en de enige andere plek waar hij kon staan is React —
 * waar hij een herrender niet overleeft, precies wanneer je hem nodig hebt.
 */
const verbruikteSleutels = new Set<string>();

/**
 * Geeft een sleutel weer vrij nadat BEWEZEN is dat er niets is geschreven.
 *
 * LET OP — hier zat een gat. Deze functie werd aangeroepen bij élke `onError`,
 * en dat is precies verkeerd bij de fout waar het om gaat: bij een netwerk- of
 * timeoutfout heeft de INSERT Postgres wél kunnen bereiken. De sleutel kwam dan
 * vrij, de dialoog bleef staan met een werkende knop, en een tweede klik maakte
 * een tweede rij — het duplicaat waar dit hele mechanisme voor bestaat (één
 * extra SPULA-tarief tilt intake 6daab7ab van EUR 212.000 naar EUR 270.000).
 *
 * Vrijgeven mag alleen als de database het statement aantoonbaar heeft
 * afgewezen. `isZekerNietsGeschreven` in `subsidie-schrijfrecht.ts` trekt die
 * grens; `useSubsidieBeheer` roept deze functie alleen aan als die true geeft.
 */
export function geefBevestigingVrij(sleutel: string | null | undefined): void {
  if (typeof sleutel === 'string') verbruikteSleutels.delete(sleutel);
}

/** Alleen voor tests: de teller leegmaken tussen gevallen. */
export function vergeetVerbruikteSleutels(): void {
  verbruikteSleutels.clear();
}

/**
 * De grendel vóór elke schrijfactie.
 *
 * Hij staat hier en niet in de JSX omdat een bevestiging die alleen in het
 * scherm bestaat geen bevestiging is: een knop die per ongeluk twee keer vuurt,
 * een dialoog die door een herrender opnieuw opent, of een latere aanroeper die
 * de stap overslaat, komt dan gewoon langs. De mutatie in `useSubsidieBeheer`
 * roept deze functie aan vóór hij Supabase aanraakt.
 *
 * Hij toetst DRIE dingen, en de eerste was er eerst niet:
 *
 *  1. HOORT DE VOORVERTONING BIJ DIT VOORSTEL? De grendel kreeg de velden die
 *     geschreven werden nergens te zien. Een voorvertoning van "alleen de
 *     beschrijving wijzigen" geeft `bevestigingSleutel === null` en liet
 *     daarmee élk payload door. Via het scherm niet uit te buiten, maar de
 *     grendel bewees niets. Nu wordt de vingerafdruk uit het voorstel
 *     herrekend en naast die van de voorvertoning gelegd.
 *  2. IS DE STAND NOG DEZELFDE? Dit was het tweede gat, en het zwaarste: de
 *     afdruk hing aan het voorstel en aan de uitkomst, maar nergens aan de
 *     CATALOGUS waarop die uitkomst is gemeten. `gezienOp` dekt alleen de ene
 *     rij die je schrijft, dus een collega die ándere rijen wijzigde bleef
 *     onzichtbaar — gemeten: -EUR 42.345 die er zo doorheen ging. De aanroeper
 *     haalt daarom een VERSE afdruk op vlak vóór het schrijven; wijkt die af,
 *     dan is de meting verlopen.
 *  3. IS ER BEVESTIGD, als dat nodig was? Wijzigt het formulier daarna nog
 *     iets, dan rekent de voorvertoning opnieuw, komt er een andere sleutel
 *     uit en is de oude bevestiging waardeloos.
 *  4. IS DEZE BEVESTIGING NOG ONGEBRUIKT? Zie `verbruikteSleutels`.
 */
export function bewaakBevestiging(
  v: Voorvertoning,
  voorstel: Voorstel,
  verseCatalogusAfdruk: string,
  gegeven: string | null | undefined,
): void {
  // 1. HOORT DEZE VOORVERTONING BIJ DIT PAYLOAD?
  const verwacht =
    v.soort === 'nieuwe-regeling'
      ? vingerafdruk(JSON.stringify(['nieuwe-regeling', voorstel, v.catalogusAfdruk]))
      : vingerafdruk(JSON.stringify(
        [voorstel, v.geraakt, v.duplicaat, v.plafondWissel, v.catalogusAfdruk],
      ));

  if (verwacht !== v.voorstelAfdruk) {
    throw new NietBevestigd(
      'Er is NIETS opgeslagen: de voorvertoning die hierbij hoort gaat over een andere wijziging dan wat '
      + 'er nu opgeslagen zou worden. Doe de wijziging opnieuw, zodat je ziet wat er werkelijk verschuift.',
    );
  }

  // 2. IS DE STAND NOG DEZELFDE ALS WAAROP GEMETEN IS?
  if (verseCatalogusAfdruk !== v.catalogusAfdruk) {
    throw new NietBevestigd(
      'Er is NIETS opgeslagen: iemand anders heeft de subsidiedata gewijzigd sinds deze voorvertoning is '
      + 'gemaakt. De bedragen die je zag zijn daarmee verlopen — ze zijn gerekend op een stand die niet '
      + 'meer bestaat. Bekijk de wijziging opnieuw; dan zie je wat hij nú doet.',
    );
  }

  if (v.bevestigingSleutel === null) return;

  // 3. IS ER BEVESTIGD?
  if (gegeven !== v.bevestigingSleutel) {
    throw new NietBevestigd(
      'Er is NIETS opgeslagen: deze wijziging verschuift een uitkomst in een lopend dossier en is niet '
      + 'bevestigd. Bekijk de voorvertoning en bevestig daar; wijzig je daarna nog iets, dan vervalt de '
      + 'eerdere bevestiging en wordt er opnieuw gerekend.',
    );
  }

  // 4. IS DEZE BEVESTIGING NOG ONGEBRUIKT?
  if (verbruikteSleutels.has(gegeven)) {
    throw new NietBevestigd(
      'Er is NIETS opgeslagen: deze bevestiging is al gebruikt. Een bevestiging geldt voor één handeling — '
      + 'anders zou een tweede klik op "toevoegen" een tweede rij maken. Bekijk de voorvertoning opnieuw '
      + 'als je dit echt nog een keer wilt doen.',
    );
  }
  verbruikteSleutels.add(gegeven);
}
